import React, { useEffect } from "react";
import { gsap } from "gsap";
import styles from "./DefaultLayout.module.scss";
import { useSwiperSlide } from "swiper/react";
import { AssetParser } from "../AssetParser/AssetParser";
import ConditionalLink from "../ConditionalLink/ConditionalLink";

export type ResponsiveImage = {
  aspectRatio: number;
  alt: string;
  height: number;
  width: number;
  sizes: string;
  src: string;
  srcSet: string;
  webpSrcSet: string;
};

export type Asset = {
  relativeLink?: string;
  videoLoop?: boolean;
  videoMuted?: boolean;
  asset: {
    height: number;
    width: number;
    title: string;
    url: string;
    format: string;
    responsiveImage: ResponsiveImage;
    lowRes: string;
    mediumRes: string;
  };
};
type DefaultLayoutProps = {
  assetRight: Asset[];
  assetLeft: Asset[];
  assetCenter: Asset[];
  backgroundAsset: Asset[];
  backgroundColour: { hex: string };
  firstAssetDelay: number;
  followingAssetsDelay: number;
};

const DefaultLayout: React.FC<DefaultLayoutProps> = (props) => {
  const {
    assetRight,
    assetLeft,
    assetCenter,
    backgroundAsset,
    backgroundColour,
    firstAssetDelay = 1,
    followingAssetsDelay = 1,
  } = props;
  const swiperSlide = useSwiperSlide();
  useEffect(() => {
    if (swiperSlide.isActive) {
      gsap.to(".image", {
        opacity: 1,
        duration: 1,
        delay: firstAssetDelay,
        stagger: followingAssetsDelay,
      });
    } else if (swiperSlide.isNext && swiperSlide.isPrev) {
      gsap.to(".image", {
        opacity: 0,
        duration: 0.3,
      });
    }
  }, [swiperSlide, firstAssetDelay, followingAssetsDelay]);
  if (swiperSlide.isActive || swiperSlide.isNext || swiperSlide.isPrev) {
    return (
      <div
        className={styles.Background}
        style={
          backgroundColour ? { backgroundColor: backgroundColour.hex } : {}
        }
      >
        {backgroundAsset[0]?.asset && (
          <AssetParser
            assetOptions={backgroundAsset[0]}
            active={swiperSlide.isActive}
            className={`${styles.backgroundAsset} ${styles.image}`}
            isBackgroundVideo
          />
        )}
        {assetLeft[0]?.asset && (
          <ConditionalLink
            href={assetLeft[0]?.relativeLink}
            condition={assetLeft[0]?.relativeLink ? true : false}
          >
            <AssetParser
              assetOptions={assetLeft[0]}
              active={swiperSlide.isActive}
              className={`${swiperSlide.isActive ? "image" : ""} ${
                styles.asset_left
              } ${styles.image}`}
            />
          </ConditionalLink>
        )}
        {assetCenter[0]?.asset && (
          <ConditionalLink
            href={assetCenter[0]?.relativeLink}
            condition={assetCenter[0]?.relativeLink ? true : false}
          >
            <AssetParser
              assetOptions={assetCenter[0]}
              active={swiperSlide.isActive}
              className={`${swiperSlide.isActive ? "image" : ""} ${
                styles.asset_center
              } ${styles.image}`}
            />
          </ConditionalLink>
        )}
        {assetRight[0]?.asset && (
          <ConditionalLink
            href={assetRight[0]?.relativeLink}
            condition={assetRight[0]?.relativeLink ? true : false}
          >
            <AssetParser
              assetOptions={assetRight[0]}
              active={swiperSlide.isActive}
              className={`${swiperSlide.isActive ? "image" : ""} ${
                styles.asset_right
              } ${styles.image}`}
            />
          </ConditionalLink>
        )}
      </div>
    );
  } else {
    return <></>;
  }
};

export default DefaultLayout;
